// Jixxeo Mirroring: WebRTC one-way stream from a sender to a paired TV receiver page.
const ICE = [{ urls: ["stun:stun.l.google.com:19302", "stun:stun1.l.google.com:19302"] }];
const wsUrl = (q) => window.location.origin.replace(/^http/, "ws") + "/api/ws/mirror?" + new URLSearchParams(q);

export function detectCaps() {
  const ua = navigator.userAgent;
  const v = document.createElement("video");
  const platform = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) ? "ios" : /Android/.test(ua) ? "android"
    : /Windows/.test(ua) ? "windows" : /Macintosh/.test(ua) ? "mac" : /CrOS/.test(ua) ? "chromeos" : "other";
  const browser = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : /SamsungBrowser/.test(ua) ? "Samsung Internet" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "Browser";
  return {
    platform, browser,
    screen: !!navigator.mediaDevices?.getDisplayMedia,
    webrtc: !!window.RTCPeerConnection,
    capture: !!(v.captureStream || v.mozCaptureStream),
    presentation: !!window.PresentationRequest,
    remote: "remote" in HTMLMediaElement.prototype,
    airplay: !!window.WebKitPlaybackTargetAvailabilityEvent,
  };
}

export class MirrorSender {
  constructor(code, emit) { this.code = code; this.emit = emit; this.ws = null; this.pc = null; this.stream = null; }

  connect() {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(wsUrl({ role: "sender", code: this.code }));
      this.ws = ws;
      ws.onmessage = async (e) => {
        const m = JSON.parse(e.data);
        if (m.t === "paired") { this.emit("paired"); resolve(); if (this.stream) this.offer(); }
        else if (m.t === "error") { reject(new Error(m.error === "auth" ? "Sign in to cast" : "No TV found with that code")); }
        else if (m.t === "signal") this.onSignal(m.data);
        else if (m.t === "tv_left") this.emit("tv_left");
        else if (m.t === "replaced") this.emit("replaced");
      };
      ws.onerror = () => reject(new Error("Connection failed"));
      ws.onclose = () => this.emit("closed");
      this.ping = setInterval(() => this.send({ t: "ping" }), 20000);
    });
  }

  send(m) { if (this.ws?.readyState === 1) this.ws.send(JSON.stringify(m)); }

  async setStream(stream, info) {
    this.stream = stream;
    this.send({ t: "info", info });
    if (this.ws?.readyState === 1) await this.offer();
  }

  async offer() {
    try { this.pc?.close(); } catch {}
    const pc = new RTCPeerConnection({ iceServers: ICE });
    this.pc = pc;
    this.stream.getTracks().forEach((t) => pc.addTrack(t, this.stream));
    pc.onicecandidate = (e) => e.candidate && this.send({ t: "signal", data: { candidate: e.candidate } });
    pc.onconnectionstatechange = () => this.emit("state", pc.connectionState);
    pc.getSenders().forEach((s) => {
      if (s.track?.kind !== "video") return;
      const p = s.getParameters(); p.encodings = p.encodings?.length ? p.encodings : [{}];
      p.encodings[0].maxBitrate = 6_000_000; s.setParameters(p).catch(() => {});
    });
    const o = await pc.createOffer(); await pc.setLocalDescription(o);
    this.send({ t: "signal", data: { sdp: pc.localDescription } });
  }

  async onSignal(d) {
    if (!this.pc) return;
    try { if (d.sdp) await this.pc.setRemoteDescription(d.sdp); else if (d.candidate) await this.pc.addIceCandidate(d.candidate); } catch {}
  }

  stop() {
    this.send({ t: "stop" });
    clearInterval(this.ping);
    try { this.pc?.close(); } catch {}
    try { this.ws?.close(); } catch {}
  }
}

export class MirrorReceiver {
  constructor(code, emit) { this.code = code; this.emit = emit; this.pc = null; this.closed = false; this.pending = []; }

  connect() {
    const ws = new WebSocket(wsUrl({ role: "tv", ...(this.code ? { code: this.code } : {}) }));
    this.ws = ws;
    ws.onmessage = async (e) => {
      const m = JSON.parse(e.data);
      if (m.t === "code") { this.code = m.code; this.emit("code", m.code); }
      else if (m.t === "paired") this.emit("paired", m.name);
      else if (m.t === "info") this.emit("info", m.info);
      else if (m.t === "signal") this.onSignal(m.data);
      else if (m.t === "stop" || m.t === "sender_left") { try { this.pc?.close(); } catch {} this.pc = null; this.emit("stopped"); }
    };
    ws.onclose = () => { this.emit("disconnected"); if (!this.closed) setTimeout(() => !this.closed && this.connect(), 2000); };
    clearInterval(this.ping); this.ping = setInterval(() => ws.readyState === 1 && ws.send(JSON.stringify({ t: "ping" })), 20000);
  }

  async onSignal(d) {
    try {
      if (d.sdp?.type === "offer") {
        try { this.pc?.close(); } catch {}
        const pc = new RTCPeerConnection({ iceServers: ICE });
        this.pc = pc;
        pc.ontrack = (e) => this.emit("stream", e.streams[0]);
        pc.onicecandidate = (e) => e.candidate && this.ws.send(JSON.stringify({ t: "signal", data: { candidate: e.candidate } }));
        await pc.setRemoteDescription(d.sdp);
        for (const c of this.pending.splice(0)) await pc.addIceCandidate(c).catch(() => {});
        const a = await pc.createAnswer(); await pc.setLocalDescription(a);
        this.ws.send(JSON.stringify({ t: "signal", data: { sdp: pc.localDescription } }));
      } else if (d.candidate) {
        if (this.pc?.remoteDescription) await this.pc.addIceCandidate(d.candidate); else this.pending.push(d.candidate);
      }
    } catch {}
  }

  destroy() { this.closed = true; clearInterval(this.ping); try { this.pc?.close(); } catch {} try { this.ws?.close(); } catch {} }
}
